import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { useRecentlyViewed } from "@/lib/recentlyViewed";
import { useVehicle } from "@/lib/vehicle";
import api from "@/lib/api";

/**
 * RecentlyViewed strip.
 *
 * - Reads the last-viewed product slugs from localStorage.
 * - Fetches the up-to-date product records so prices/images/stock reflect today's catalog.
 * - Excludes `excludeSlug` (the page's current product) so the strip never repeats what you're on.
 * - If a vehicle context is set, products whose compatibility matches that brand are pinned first.
 */
export function RecentlyViewed({ excludeSlug, title = "Recently viewed", limit = 6 }) {
    const recent = useRecentlyViewed();
    const vehicle = useVehicle();
    const [productMap, setProductMap] = useState({});
    const [loading, setLoading] = useState(true);

    const slugs = useMemo(
        () => recent.map((r) => r.slug).filter((s) => s && s !== excludeSlug).slice(0, 12),
        [recent, excludeSlug],
    );

    useEffect(() => {
        if (slugs.length === 0) { setLoading(false); return; }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/products", { params: { limit: 300 } });
                if (cancelled) return;
                const map = {};
                data.items.forEach((p) => { map[p.slug] = p; });
                setProductMap(map);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [slugs.length]);

    const items = useMemo(() => {
        const resolved = slugs.map((s) => productMap[s]).filter(Boolean);
        if (!vehicle?.slug) return resolved.slice(0, limit);
        const matches = resolved.filter((p) => (p.compatibility || []).includes(vehicle.slug));
        const rest = resolved.filter((p) => !(p.compatibility || []).includes(vehicle.slug));
        return [...matches, ...rest].slice(0, limit);
    }, [slugs, productMap, vehicle, limit]);

    if (loading || items.length === 0) return null;

    return (
        <section className="border-t-2 border-[hsl(var(--dust-300))] bg-[hsl(var(--dust-50))]" data-testid="recently-viewed">
            <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
                <div className="flex items-end justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">
                            <Clock className="h-3.5 w-3.5" /> Trail memory
                        </div>
                        <h2 className="mt-1 font-display text-3xl uppercase italic text-[hsl(var(--ink))] md:text-4xl">
                            {title}
                        </h2>
                        {vehicle?.slug && (
                            <p className="mt-1 font-mono-brand text-xs text-[hsl(var(--ink-soft))]">
                                Prioritized for {vehicle.name || vehicle.slug} builds.
                            </p>
                        )}
                    </div>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
                    {items.map((p) => (
                        <Link
                            key={p.slug}
                            to={`/shop/${p.slug}`}
                            className="group block border-2 border-[hsl(var(--ink))] bg-card p-3 transition-transform hover:-translate-y-0.5"
                            data-testid={`recently-viewed-${p.slug}`}
                        >
                            <div className="aspect-square overflow-hidden bg-[hsl(var(--dust-100))]">
                                {(p.images && p.images[0]) ? (
                                    <img src={p.images[0]} alt={p.name} className="h-full w-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
                                ) : (
                                    <div className="flex h-full items-center justify-center font-mono-brand text-[10px] uppercase text-[hsl(var(--ink-soft))]">No image</div>
                                )}
                            </div>
                            <div className="mt-2 line-clamp-2 font-heading text-xs uppercase tracking-wide text-[hsl(var(--ink))]">
                                {p.name}
                            </div>
                            <div className="mt-1 font-mono-brand text-xs text-[hsl(var(--ink-soft))]">
                                ${Number(p.price || 0).toFixed(2)}
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
